import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { invokeFunction } from '../lib/functionsClient';
import type { Project } from '../types/api';

type ProjectsValue = {
  projects: Project[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setProject: (project: Project) => void;
};

const ProjectsContext = createContext<ProjectsValue | null>(null);

export const ProjectsProvider = ({ children }: { children: React.ReactNode }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fnError } = await invokeFunction<{ projects: Project[] }>('create-project', {
      body: { action: 'list' },
    });

    if (fnError) {
      setError(fnError);
      setLoading(false);
      return;
    }

    setProjects(data?.projects ?? []);
    setLoading(false);
  }, []);

  const setProject = useCallback((project: Project) => {
    setProjects((prev) => {
      const existingIndex = prev.findIndex((p) => p.id === project.id);
      if (existingIndex === -1) {
        return [project, ...prev];
      }
      const next = [...prev];
      next[existingIndex] = project;
      return next;
    });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(() => ({ projects, loading, error, refresh, setProject }), [projects, loading, error, refresh, setProject]);

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
};

export const useProjects = () => {
  const ctx = useContext(ProjectsContext);
  if (!ctx) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }
  return ctx;
};

